import type { Parcela } from "@/hooks/useParcelas";
import type { TransacaoFinanceira } from "@/hooks/useFinanceiro";

export type FinanceiroLinha = {
  id: number;
  pedido_id?: number | null;
  pedido?: { id: number; numero_pedido?: string };
  cliente_id?: number | null;
  cliente?: { nome?: string };
  fornecedor?: { nome?: string };
  fornecedor_id?: number | null;
  tipo?: "entrada" | "saida";
  numero_parcela?: number;
  total_parcelas?: number;
  valor: number;
  forma_pagamento?: string;
  status: string;
  data_vencimento?: string;
  data_pagamento?: string | null;
  observacoes?: string;
  updated_at?: string;
  nota_fiscal_url?: string | null;
  notas_fiscais?: unknown;
  _parcela?: Parcela;
  _origem: "parcela" | "transacao";
};

export function parcelaParaLinha(p: Parcela): FinanceiroLinha {
  return {
    id: p.id,
    pedido_id: p.pedido_id,
    pedido: p.pedido,
    cliente_id: p.cliente_id,
    cliente: p.cliente,
    tipo: "entrada",
    numero_parcela: p.numero_parcela,
    total_parcelas: p.total_parcelas,
    valor: p.valor,
    forma_pagamento: p.forma_pagamento,
    status: p.status,
    data_vencimento: p.data_vencimento,
    data_pagamento: p.data_pagamento ?? null,
    observacoes: p.observacoes,
    _parcela: p,
    _origem: "parcela",
  };
}

export function transacaoParaLinha(t: TransacaoFinanceira): FinanceiroLinha {
  return {
    id: t.id,
    pedido_id: t.pedido_id,
    pedido: t.pedido,
    cliente_id: t.cliente_id,
    cliente: t.cliente,
    fornecedor_id: t.fornecedor_id,
    fornecedor: t.fornecedor,
    tipo: t.tipo,
    numero_parcela: t.numero_parcela,
    total_parcelas: t.total_parcelas,
    valor: t.valor,
    forma_pagamento: t.forma_pagamento,
    status: t.status,
    data_vencimento: t.data_vencimento,
    data_pagamento: t.data_pagamento ?? null,
    observacoes: t.observacoes,
    updated_at: t.updated_at,
    nota_fiscal_url: t.nota_fiscal_url,
    notas_fiscais: t.notas_fiscais,
    _origem: "transacao",
  };
}

export function linhaRecebivelKey(linha: FinanceiroLinha): string {
  return `${linha._origem}-${linha.id}`;
}

/** Chave de negócio: 1 cobrança = pedido (ou contrato) + nº da parcela */
export function chaveCobrancaRecebivel(linha: {
  pedido_id?: number | null;
  pedido?: { id?: number } | null;
  cliente_id?: number | null;
  numero_parcela?: number | null;
  contrato_id?: number | null;
  _parcela?: { contrato_id?: number | null } | null;
  _origem?: string;
  id?: number;
}): string {
  const pedidoId = linha.pedido_id ?? linha.pedido?.id ?? null;
  const num = linha.numero_parcela ?? 1;
  if (pedidoId != null) return `pedido:${pedidoId}:parcela:${num}`;

  const contratoId =
    linha.contrato_id ?? linha._parcela?.contrato_id ?? null;
  if (contratoId != null) {
    return `contrato:${contratoId}:parcela:${num}`;
  }

  // Sem pedido/contrato: não deduplicar com outras linhas
  return `avulso:${linha._origem ?? "x"}:${linha.id ?? 0}`;
}

function isEspelhoAutomatico(obs?: string | null): boolean {
  return (obs || "").includes("Recebimento automático cartão");
}

/** Maior score = linha preferida na listagem unificada */
function scoreLinhaRecebivel(linha: FinanceiroLinha): number {
  let score = 0;
  // Transação financeira é a fonte do fluxo de caixa / cards
  if (linha._origem === "transacao") score += 1000;
  if (linha.status === "pago") score += 100;
  // Evitar espelhos do cron quando já existe a original
  if (isEspelhoAutomatico(linha.observacoes)) score -= 200;
  // Preferir o registro mais antigo (menor id) como original
  score -= Math.min(linha.id, 1_000_000) / 1_000_000;
  return score;
}

function compareRecebiveis(a: FinanceiroLinha, b: FinanceiroLinha): number {
  const da = a.data_vencimento ?? "";
  const db = b.data_vencimento ?? "";
  if (da !== db) return da.localeCompare(db);
  if (a._origem !== b._origem) return a._origem.localeCompare(b._origem);
  return a.id - b.id;
}

/**
 * Contas a receber: apenas transações financeiras (1 linha por pedido+parcela).
 * Parcelas de contrato NÃO entram — evitam linhas "Contrato" e distorção quando
 * o filtro de vencimento pega a parcela e deixa a TF de fora (datas diferentes).
 */
export function mergeContasAReceber(
  _parcelas: Parcela[] | null | undefined,
  transacoes: TransacaoFinanceira[]
): FinanceiroLinha[] {
  void _parcelas;
  const linhas = transacoes.map(transacaoParaLinha);

  const porChave = new Map<string, FinanceiroLinha>();
  for (const linha of linhas) {
    const chave = chaveCobrancaRecebivel(linha);
    const atual = porChave.get(chave);
    if (!atual || scoreLinhaRecebivel(linha) > scoreLinhaRecebivel(atual)) {
      porChave.set(chave, linha);
    }
  }

  return Array.from(porChave.values()).sort(compareRecebiveis);
}

export type FinanceiroSortField =
  | "id"
  | "cliente"
  | "pedido"
  | "parcela"
  | "tipo"
  | "valor"
  | "forma"
  | "status"
  | "vencimento"
  | "pagamento";

export type FinanceiroSort = {
  field: FinanceiroSortField;
  direction: "asc" | "desc";
};

function sortValue(linha: FinanceiroLinha, field: FinanceiroSortField): string | number {
  switch (field) {
    case "id":
      return Number(linha.id) || 0;
    case "cliente":
      return (
        linha.fornecedor?.nome ||
        linha.cliente?.nome ||
        (linha.fornecedor_id ? `F${linha.fornecedor_id}` : "") ||
        (linha.cliente_id ? `C${linha.cliente_id}` : "") ||
        ""
      ).toLowerCase();
    case "pedido": {
      const id = linha.pedido_id ?? linha.pedido?.id;
      return id != null ? Number(id) : 0;
    }
    case "parcela":
      return Number(linha.numero_parcela) || 0;
    case "tipo":
      return (linha.tipo || "").toLowerCase();
    case "valor":
      return Number(linha.valor) || 0;
    case "forma":
      return (linha.forma_pagamento || "").toLowerCase();
    case "status":
      return (linha.status || "").toLowerCase();
    case "vencimento":
      return linha.data_vencimento || "";
    case "pagamento":
      return linha.data_pagamento || "";
  }
}

/** Ordena linhas financeiras pelo campo escolhido (antes da paginação). */
export function sortFinanceiroLinhas<T extends FinanceiroLinha>(
  linhas: T[],
  sort: FinanceiroSort | null | undefined
): T[] {
  if (!sort) return linhas;
  const dir = sort.direction === "asc" ? 1 : -1;
  return [...linhas].sort((a, b) => {
    const va = sortValue(a, sort.field);
    const vb = sortValue(b, sort.field);
    let cmp = 0;
    if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
    else cmp = String(va).localeCompare(String(vb), "pt-BR", { numeric: true });
    if (cmp === 0) cmp = a.id - b.id;
    return cmp * dir;
  });
}

/** Mapeia campo de UI para coluna do banco (quando aplicável). */
export function financeiroSortToDbColumn(field: FinanceiroSortField): string | null {
  switch (field) {
    case "id":
      return "id";
    case "tipo":
      return "tipo";
    case "valor":
      return "valor";
    case "forma":
      return "forma_pagamento";
    case "status":
      return "status";
    case "vencimento":
      return "data_vencimento";
    case "pagamento":
      return "data_pagamento";
    default:
      return null; // cliente/pedido/parcela: ordenar em memória
  }
}

/**
 * Deduplica transações de entrada para cards/stats (mesmo pedido + nº parcela).
 * Preferência: sem marcador de espelho automático, pago, menor id.
 */
export function dedupeTransacoesEntradaParaStats<
  T extends {
    id: number;
    tipo?: string;
    pedido_id?: number | null;
    contrato_id?: number | null;
    numero_parcela?: number | null;
    status?: string;
    observacoes?: string | null;
  },
>(transacoes: T[]): T[] {
  const entradas = transacoes.filter((t) => t.tipo === "entrada");
  const outras = transacoes.filter((t) => t.tipo !== "entrada");

  const porChave = new Map<string, T>();
  for (const t of entradas) {
    const chave = chaveCobrancaRecebivel({
      pedido_id: t.pedido_id,
      contrato_id: t.contrato_id,
      numero_parcela: t.numero_parcela,
      id: t.id,
      _origem: "transacao",
    });
    const atual = porChave.get(chave);
    if (!atual) {
      porChave.set(chave, t);
      continue;
    }
    const score = (x: T) => {
      let s = 0;
      if (x.status === "pago") s += 100;
      if (isEspelhoAutomatico(x.observacoes)) s -= 200;
      s -= Math.min(x.id, 1_000_000) / 1_000_000;
      return s;
    };
    if (score(t) > score(atual)) porChave.set(chave, t);
  }

  return [...outras, ...porChave.values()];
}
